import { useCallback, useEffect, useRef, useState } from 'react';
import { TTS_HEADER, TTS_HEADER_SIZE } from '../constants/audioProtocol';

export interface UseTTSPlaybackOptions {
  ws: WebSocket | null;
  pauseVAD: () => void;
  resumeVAD: () => void;
}

export interface UseTTSPlaybackReturn {
  isPlaying: boolean;
  lastSegmentId: string | null;
  clearQueue: () => void;
}

/** Additional silence buffer after TTS durationMs before resuming VAD (ms). */
const POST_PLAYBACK_BUFFER_MS = 300;

/**
 * Converts 16 raw bytes to a hyphenated UUID string.
 * Format: xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx
 */
function bytesToUuid(bytes: Uint8Array): string {
  const hex = Array.from(bytes)
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
  return [
    hex.slice(0, 8),
    hex.slice(8, 12),
    hex.slice(12, 16),
    hex.slice(16, 20),
    hex.slice(20, 32),
  ].join('-');
}

/**
 * Parses a binary TTS frame received from the backend:
 *   byte 0     : 0x02 header (already checked by caller)
 *   bytes 1–16 : segment UUID (16 bytes)
 *   bytes 17–20: durationMs (uint32 big-endian)
 *   byte  21   : direction (0x01 = doctor→patient, 0x02 = patient→doctor)
 *   bytes 22+  : MP3 audio data
 */
interface ParsedTTSFrame {
  segmentId: string;
  durationMs: number;
  direction: number;
  mp3: ArrayBuffer;
}

function parseTTSFrame(buffer: ArrayBuffer): ParsedTTSFrame | null {
  if (buffer.byteLength < TTS_HEADER_SIZE + 1) {
    return null;
  }
  const view = new DataView(buffer);
  if (view.getUint8(0) !== TTS_HEADER) {
    return null;
  }

  const uuidBytes = new Uint8Array(buffer, 1, 16);
  const segmentId = bytesToUuid(uuidBytes);
  const durationMs = view.getUint32(17, false /* big-endian */);
  const direction = view.getUint8(21);
  const mp3 = buffer.slice(TTS_HEADER_SIZE);

  return { segmentId, durationMs, direction, mp3 };
}

/**
 * useTTSPlayback — listens for binary TTS frames on the WebSocket,
 * decodes MP3 audio, plays it through the device speaker, and manages
 * VAD suppression during playback.
 *
 * Protocol (binary frame with 0x02 header):
 *   [0x02][16-byte UUID][4-byte uint32 BE durationMs][1-byte direction][MP3...]
 *
 * Behaviour:
 *   1. Receive binary message → check 0x02 header → parse frame.
 *   2. Call pauseVAD() before decoding to suppress microphone feedback detection.
 *   3. Decode MP3 via AudioContext.decodeAudioData.
 *   4. Enqueue decoded AudioBuffer into sequential playback queue.
 *   5. Play clips one-by-one in arrival order — no stomping.
 *   6. After last clip finishes: wait POST_PLAYBACK_BUFFER_MS, then resumeVAD() and send tts:done.
 */
export function useTTSPlayback({
  ws,
  pauseVAD,
  resumeVAD,
}: UseTTSPlaybackOptions): UseTTSPlaybackReturn {
  const [isPlaying, setIsPlaying] = useState(false);
  const [lastSegmentId, setLastSegmentId] = useState<string | null>(null);

  const audioContextRef = useRef<AudioContext | null>(null);
  const sourceNodeRef = useRef<AudioBufferSourceNode | null>(null);
  const resumeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Stable refs for callbacks and ws so that closures (setTimeout, decodeAudioData
  // callbacks) always access the current values even after ws reconnects or prop changes.
  const wsRef = useRef<WebSocket | null>(ws);
  useEffect(() => {
    wsRef.current = ws;
  }, [ws]);

  const pauseVADRef = useRef(pauseVAD);
  const resumeVADRef = useRef(resumeVAD);
  useEffect(() => {
    pauseVADRef.current = pauseVAD;
  }, [pauseVAD]);
  useEffect(() => {
    resumeVADRef.current = resumeVAD;
  }, [resumeVAD]);

  /** Sequential playback queue — clips play in order, no stomping. */
  const clipQueueRef = useRef<Array<{
    audioBuffer: AudioBuffer;
    segmentId: string;
    durationMs: number;
    direction: number;
  }>>([]);
  const isPlayingClipRef = useRef(false);

  /**
   * Arrival-order gating for decodeAudioData race condition.
   *
   * decodeAudioData is async and smaller payloads decode faster than larger
   * ones. When two sub-sequence frames arrive in the same tick (e.g. 48KB
   * sentence + 8KB sentence), the 8KB frame finishes decoding first and would
   * be pushed to the FIFO queue before the 48KB frame — reversing playback
   * order.
   *
   * Fix: assign a monotonic arrivalSeq synchronously on receipt, store decoded
   * results in a pending map, then flush to the playback queue strictly in
   * arrival order.
   */
  const arrivalSeqRef = useRef(0);
  const nextFlushSeqRef = useRef(1);
  const pendingDecodeMapRef = useRef<Map<number, {
    audioBuffer: AudioBuffer;
    segmentId: string;
    durationMs: number;
    direction: number;
  }>>(new Map());

  /** Lazily create or resume a shared AudioContext. */
  const getAudioContext = useCallback((): AudioContext => {
    if (!audioContextRef.current || audioContextRef.current.state === 'closed') {
      audioContextRef.current = new AudioContext();
    }
    if (audioContextRef.current.state === 'suspended') {
      // Resume suspended context (required after user gesture in some browsers).
      audioContextRef.current.resume().catch(() => undefined);
    }
    return audioContextRef.current;
  }, []);

  const clearResumeTimer = useCallback(() => {
    if (resumeTimerRef.current !== null) {
      clearTimeout(resumeTimerRef.current);
      resumeTimerRef.current = null;
    }
  }, []);

  /**
   * Play the next clip from the queue. Called when:
   * - A new clip is enqueued and nothing is currently playing
   * - The current clip finishes playing (source.onended)
   */
  const playNextClip = useCallback(() => {
    const queue = clipQueueRef.current;
    if (queue.length === 0) {
      // Queue empty — all clips played. Resume VAD and send tts:done.
      isPlayingClipRef.current = false;
      setIsPlaying(false);

      // Schedule VAD resume with the post-playback buffer
      resumeTimerRef.current = setTimeout(() => {
        resumeTimerRef.current = null;
        resumeVADRef.current();
        const currentWs = wsRef.current;
        if (currentWs && currentWs.readyState === WebSocket.OPEN) {
          currentWs.send(JSON.stringify({ type: 'tts:done' }));
        }
      }, POST_PLAYBACK_BUFFER_MS);
      return;
    }

    const clip = queue.shift()!;
    isPlayingClipRef.current = true;

    const ctx = getAudioContext();
    const source = ctx.createBufferSource();
    source.buffer = clip.audioBuffer;
    source.connect(ctx.destination);
    sourceNodeRef.current = source;

    setIsPlaying(true);
    setLastSegmentId(clip.segmentId);

    source.onended = () => {
      sourceNodeRef.current = null;
      // Play next clip immediately — no gap between clips
      playNextClip();
    };

    source.start();
  }, [getAudioContext]);

  /** Clear the playback queue and stop current audio — used when doctor starts speaking. */
  const clearQueue = useCallback(() => {
    clipQueueRef.current = [];
    pendingDecodeMapRef.current.clear();
    arrivalSeqRef.current = 0;
    nextFlushSeqRef.current = 1;
    clearResumeTimer();
    if (sourceNodeRef.current) {
      try {
        sourceNodeRef.current.stop();
      } catch {
        // Already stopped
      }
      sourceNodeRef.current = null;
    }
    isPlayingClipRef.current = false;
    setIsPlaying(false);
    resumeVADRef.current();
  }, [clearResumeTimer]);

  // Handle incoming WebSocket messages.
  useEffect(() => {
    if (!ws) return;

    const handleMessage = (event: MessageEvent) => {
      // TTS frames are binary (ArrayBuffer or Blob).
      if (!(event.data instanceof ArrayBuffer) && !(event.data instanceof Blob)) {
        return;
      }

      const processBuffer = (buffer: ArrayBuffer) => {
        if (buffer.byteLength === 0) return;
        const firstByte = new Uint8Array(buffer)[0];
        if (firstByte !== TTS_HEADER) return;

        const frame = parseTTSFrame(buffer);
        if (!frame) {
          console.warn('[useTTSPlayback] Received malformed TTS frame — ignoring.');
          return;
        }

        const { segmentId, durationMs, direction, mp3 } = frame;

        // Pause VAD on first clip arrival (if not already paused)
        if (!isPlayingClipRef.current && clipQueueRef.current.length === 0) {
          pauseVADRef.current();
          clearResumeTimer();
        }

        const ctx = getAudioContext();

        // Assign monotonic arrival sequence SYNCHRONOUSLY before async decode.
        const mySeq = ++arrivalSeqRef.current;

        ctx.decodeAudioData(
          mp3,
          (audioBuffer) => {
            // Store decoded result keyed by arrival order.
            pendingDecodeMapRef.current.set(mySeq, { audioBuffer, segmentId, durationMs, direction });

            // Flush all contiguous decoded frames in arrival order.
            const pending = pendingDecodeMapRef.current;
            while (pending.has(nextFlushSeqRef.current)) {
              const clip = pending.get(nextFlushSeqRef.current)!;
              pending.delete(nextFlushSeqRef.current);
              nextFlushSeqRef.current++;
              clipQueueRef.current.push(clip);
            }

            // If nothing is currently playing, start playback
            if (!isPlayingClipRef.current) {
              playNextClip();
            }
          },
          (decodeError) => {
            console.warn('[useTTSPlayback] MP3 decode failed:', decodeError);
            // Skip this sequence slot so subsequent frames can still flush.
            pendingDecodeMapRef.current.delete(mySeq);
            if (mySeq === nextFlushSeqRef.current) {
              nextFlushSeqRef.current++;
              // Flush any frames that were waiting behind the failed one.
              const pending = pendingDecodeMapRef.current;
              while (pending.has(nextFlushSeqRef.current)) {
                const clip = pending.get(nextFlushSeqRef.current)!;
                pending.delete(nextFlushSeqRef.current);
                nextFlushSeqRef.current++;
                clipQueueRef.current.push(clip);
              }
            }
            // If queue is empty and nothing playing, resume VAD
            if (!isPlayingClipRef.current && clipQueueRef.current.length === 0) {
              resumeVADRef.current();
            }
          },
        );
      };

      if (event.data instanceof Blob) {
        event.data.arrayBuffer().then(processBuffer).catch((err) => {
          console.warn('[useTTSPlayback] Failed to read Blob:', err);
        });
      } else {
        processBuffer(event.data as ArrayBuffer);
      }
    };

    ws.addEventListener('message', handleMessage);
    return () => {
      ws.removeEventListener('message', handleMessage);
    };
  }, [ws, getAudioContext, clearResumeTimer, playNextClip]);

  // Clean up audio resources on unmount.
  useEffect(() => {
    return () => {
      clearResumeTimer();
      clipQueueRef.current = [];
      pendingDecodeMapRef.current.clear();
      arrivalSeqRef.current = 0;
      nextFlushSeqRef.current = 1;
      isPlayingClipRef.current = false;

      if (sourceNodeRef.current) {
        try {
          sourceNodeRef.current.stop();
        } catch {
          // Ignore.
        }
        sourceNodeRef.current = null;
      }

      if (audioContextRef.current) {
        audioContextRef.current.close().catch(() => undefined);
        audioContextRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return { isPlaying, lastSegmentId, clearQueue };
}
